<template>
  <div class="dashboard-cost-analysis">
    <div class="cost-header">
      <div>
        <h3 class="cost-title">{{ $t('org-dashboard.cost-analysis') }}</h3>
        <p class="cost-subtitle">{{ $t('org-dashboard.cost-subtitle') }}</p>
      </div>
      <v-btn x-small outlined color="primary" @click="$emit('view-details')">{{ $t('org-dashboard.view-details') }}</v-btn>
    </div>

    <div class="cost-cards">
      <div
        v-for="(seg, i) in segments"
        :key="i"
        class="cost-card"
        :class="{'cost-card--disabled': seg.disabled, 'cost-card--clickable': !!seg.section}"
        :style="{'--accent': seg.color || '#3b82f6'}"
        @click="seg.section ? $emit('card-click', seg.section) : null"
      >
        <div class="cost-card-top">
          <span class="cost-card-label">
            <v-icon small :color="seg.color">{{ seg.icon || 'mdi-currency-eur' }}</v-icon>
            {{ seg.label }}
          </span>
          <span v-if="seg.change !== null && seg.change !== undefined" class="cost-card-change" :class="getChangeClass(seg.change)">
            {{ seg.change >= 0 ? '+' : '' }}{{ seg.change }}%
          </span>
        </div>
        <div class="cost-card-amount-row">
          <span class="cost-card-amount">{{ formatCurrency(seg.amount) }}</span>
          <span v-if="seg.avgCostPerKwh != null" class="cost-card-kwh-price">
            {{ formatUnitPrice(seg.avgCostPerKwh) }}
            <small>/kWh</small>
          </span>
        </div>
        <div class="cost-card-divider" :style="{backgroundColor: seg.color || '#3b82f6'}"></div>
        <div class="cost-card-avgs">
          <span>{{ $t('org-dashboard.avg-per-user') }}: <strong>{{ seg.avgPerUser !== null && seg.avgPerUser !== undefined ? formatCurrency(seg.avgPerUser) : '--' }}</strong></span>
          <span>{{ $t('org-dashboard.avg-per-session') }}: <strong>{{ seg.avgPerSession !== null && seg.avgPerSession !== undefined ? formatCurrency(seg.avgPerSession) : '--' }}</strong></span>
        </div>
      </div>
    </div>

    <div class="cost-sparkline" v-if="sparklineData && sparklineData.length">
      <highcharts :options="sparklineOptions" ref="chart" />
    </div>
  </div>
</template>

<script>
import { Chart } from 'highcharts-vue'

export default {
  name: 'DashboardCostAnalysis',
  components: {
    highcharts: Chart,
  },
  props: {
    segments: { type: Array, default: () => [] },
    sparklineData: { type: Array, default: () => [] },
    sparklineLabels: { type: Array, default: () => [] },
    currency: { type: String, default: 'EUR' },
  },

  computed: {
    sparklineOptions() {
      return {
        chart: {
          type: 'spline',
          height: 100,
          backgroundColor: 'transparent',
          margin: [10, 0, 25, 0],
        },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: false },
        xAxis: {
          categories: this.sparklineLabels,
          labels: { style: { fontSize: '10px', color: '#9ca3af' } },
          lineWidth: 0,
          tickWidth: 0,
        },
        yAxis: {
          visible: false,
          gridLineWidth: 0,
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 6,
          style: { fontSize: '11px' },
        },
        plotOptions: {
          spline: {
            marker: {
              enabled: true,
              radius: 3,
              lineWidth: 2,
              lineColor: '#FF5722',
              fillColor: 'white',
            },
            lineWidth: 2.5,
            color: '#FF5722',
          },
        },
        series: [{
          name: this.$t('org-dashboard.total-cost'),
          data: this.sparklineData,
        }],
      }
    },
  },

  methods: {
    formatCurrency(val) {
      if (val === null || val === undefined) return '-'
      return new Intl.NumberFormat(this.$i18n.locale, {
        style: 'currency',
        currency: this.currency,
      }).format(val)
    },

    formatUnitPrice(val) {
      if (val == null) return '-'
      return new Intl.NumberFormat(this.$i18n.locale, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 4,
      }).format(val)
    },

    getChangeClass(change) {
      if (change > 0) return 'cost-card-change--up'
      if (change < 0) return 'cost-card-change--down'
      return ''
    },
  },
}
</script>

<style lang="scss" scoped>
.dashboard-cost-analysis {
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.cost-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.cost-title {
  font-size: 14px;
  font-weight: 700;
  color: #1f2937;
}

.cost-subtitle {
  font-size: 11px;
  color: #6b7280;
  margin-top: 2px;
}

.cost-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.cost-card {
  padding: 16px;
  border-radius: 8px;
  background: rgba(var(--accent-rgb, 59, 130, 246), 0.03);
  border: 1px solid rgba(var(--accent-rgb, 59, 130, 246), 0.12);

  &:nth-child(1) { background: rgba(59, 130, 246, 0.03); border-color: rgba(59, 130, 246, 0.15); }
  &:nth-child(2) { background: rgba(249, 115, 22, 0.03); border-color: rgba(249, 115, 22, 0.15); }
  &:nth-child(3) { background: rgba(168, 85, 247, 0.03); border-color: rgba(168, 85, 247, 0.15); }

  &--clickable {
    cursor: pointer;
    transition: box-shadow 0.2s, transform 0.15s;
    &:hover {
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }
  }

  &--disabled {
    opacity: 0.5;
  }
}

.cost-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.cost-card-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
}

.cost-card-change {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  background: white;

  &--up {
    color: #059669;
    border: 1px solid #d1fae5;
  }
  &--down {
    color: #dc2626;
    border: 1px solid #fecaca;
  }
}

.cost-card-amount-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 8px;
}

.cost-card-amount {
  font-size: 22px;
  font-weight: 700;
  color: #1f2937;
}

.cost-card-kwh-price {
  font-size: 14px;
  font-weight: 700;
  color: #374151;

  small {
    font-size: 11px;
    font-weight: 500;
    color: #6b7280;
  }
}

.cost-card-divider {
  height: 1px;
  opacity: 0.3;
  margin-bottom: 8px;
}

.cost-card-avgs {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #4b5563;
  flex-wrap: wrap;
  gap: 4px;

  strong {
    color: #1f2937;
  }
}
</style>
